import React from "react";
import { useNavigate } from "react-router-dom";

interface Column {
  key: string;
  label: string;
  render?: (item: any) => React.ReactNode;
  isStatus?: boolean; // mark a column as status for badge coloring
}

interface AppointmentsTableCardProps {
  title: string;
  data: any[];
  navigatePath: string;
  columns: Column[];
  statusColorMap?: Record<string, string>;
  limit?: number;
}

const AppointmentsTableCard: React.FC<AppointmentsTableCardProps> = ({
  title,
  data,
  navigatePath,
  columns,
  statusColorMap = {},
  limit = 5,
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-xl shadow-md p-4 mt-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-2 border-b border-gray-300 pb-2">
        <h2 className="text-lg font-semibold text-black">{title}</h2>
        <button
          className="text-blue-600 text-sm font-medium"
          onClick={(e) => {
            e.preventDefault();
            navigate(navigatePath);
          }}
        >
          View All
        </button>
      </div>

      {/* Table */}
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="text-gray-500">
            {columns.map((col) => (
              <th key={col.key} className="py-2">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.slice(0, limit).map((item, rowIndex) => (
            <tr key={rowIndex} className="border-t">
              {columns.map((col) => {
                let content: React.ReactNode = col.render
                  ? col.render(item)
                  : item[col.key] ?? "Unknown";

                // If this is a status column, apply badge
                if (col.isStatus) {
                  const statusStr = String(content); // ensure string
                  const badgeClasses =
                    statusColorMap[statusStr] || "bg-gray-100 text-gray-700";
                  return (
                    <td key={col.key}>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClasses}`}
                      >
                        {statusStr}
                      </span>
                    </td>
                  );
                }

                return (
                  <td key={col.key} className="py-2 text-gray-700">
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}

          {data.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="py-6">
                <div className="flex flex-col items-center justify-center text-center text-gray-500 bg-gray-50 p-4 rounded-md">
                  <svg
                    className="w-10 h-10 mb-2 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3M16 7V3M3 11h18M5 20h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm font-medium">No records found</p>
                  <p className="text-xs text-gray-400">
                    Check back later or refresh the page.
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AppointmentsTableCard;
